import type {
  UserRole,
  WorkflowCapabilityId,
  WorkflowCapabilityTier,
  WorkflowSurface,
} from '@statura/types';

export type WorkflowStoryPackId =
  | 'frontline-handover-continuity'
  | 'visit-delivery-continuity'
  | 'clinical-review-follow-through'
  | 'governance-lodgement-to-closeout'
  | 'finance-statement-visibility'
  | 'tenant-workflow-configuration'
  | 'portal-messaging-visibility';

export interface WorkflowStoryPack {
  id: WorkflowStoryPackId;
  title: string;
  summary: string;
  tier: WorkflowCapabilityTier;
  primaryPersonas: readonly UserRole[];
  supportingPersonas: readonly UserRole[];
  surfaces: readonly WorkflowSurface[];
  capabilities: readonly WorkflowCapabilityId[];
  initiatingPersona: UserRole;
  closingPersona: UserRole;
  handoff: string;
  validatedBy: readonly string[];
  mandatoryBoundaries: readonly string[];
  optionalProviderBlocks: readonly string[];
  clientVisibility:
    | 'not_applicable'
    | 'internal_only'
    | 'client_visible'
    | 'client_and_family_visible';
}

export interface WorkflowStoryPackSummary {
  totalPacks: number;
  mandatoryPacks: number;
  stronglyRecommendedPacks: number;
  optionalPacks: number;
  crossAppPacks: number;
  coveredPersonas: number;
  uncoveredPersonas: readonly UserRole[];
  requiredHandoffsCovered: boolean;
}

export const FIRST_CLASS_WORKFLOW_PERSONAS: readonly UserRole[] = [
  'CARE_WORKER',
  'ENROLLED_NURSE',
  'REGISTERED_NURSE',
  'TEAM_LEADER',
  'COORDINATOR',
  'CASE_MANAGER',
  'FACILITY_MANAGER',
  'CLINICAL_LEAD',
  'QUALITY_OFFICER',
  'FINANCE_OFFICER',
  'TENANT_ADMIN',
  'CLIENT',
  'FAMILY_CARER',
] as const;

export const REQUIRED_WORKFLOW_STORY_PACK_IDS: readonly WorkflowStoryPackId[] = [
  'frontline-handover-continuity',
  'visit-delivery-continuity',
  'governance-lodgement-to-closeout',
  'finance-statement-visibility',
  'tenant-workflow-configuration',
] as const;

export const PRIORITY_WORKFLOW_HANDOFFS: readonly string[] = [
  'care worker -> RN -> team leader handover continuity',
  'coordinator -> care worker -> client visit continuity',
  'care worker -> incident -> quality officer -> client/family awareness',
  'finance officer -> statement issuance -> client portal visibility',
  'tenant admin -> workflow configuration -> role-appropriate user experience',
] as const;

export const WORKFLOW_STORY_PACKS: readonly WorkflowStoryPack[] = [
  {
    id: 'frontline-handover-continuity',
    title: 'Frontline handover continuity',
    summary:
      'Care workers, enrolled nurses, registered nurses, and team leaders can start, escalate, acknowledge, and clinically sign off handovers without breaking the mobile-to-web reviewer chain.',
    tier: 'mandatory',
    primaryPersonas: ['CARE_WORKER', 'ENROLLED_NURSE', 'REGISTERED_NURSE', 'TEAM_LEADER'],
    supportingPersonas: ['FACILITY_MANAGER'],
    surfaces: ['care-worker-app', 'web'],
    capabilities: [
      'frontline.shift.execute',
      'frontline.handover.access',
      'frontline.handover.signoff',
    ],
    initiatingPersona: 'CARE_WORKER',
    closingPersona: 'TEAM_LEADER',
    handoff: 'care worker -> RN -> team leader handover continuity',
    validatedBy: [
      'care-worker-app shift detail and handover entry',
      'care-worker-app observation and wound escalation prompts',
      'web roster handovers review surface',
      'web huddle continuity dashboard',
    ],
    mandatoryBoundaries: [
      'handover attribution and acknowledgement cannot be disabled',
      'clinical sign-off remains role-gated',
      'mobile capture does not bypass web reviewer ownership',
    ],
    optionalProviderBlocks: [
      'local handover templates',
      'dashboard emphasis',
      'escalation cadence',
    ],
    clientVisibility: 'not_applicable',
  },
  {
    id: 'visit-delivery-continuity',
    title: 'Visit delivery continuity',
    summary:
      'Coordinators, case managers, care workers, and clients see one coherent service-delivery flow from scheduling through ledgered visits and portal visibility.',
    tier: 'mandatory',
    primaryPersonas: ['COORDINATOR', 'CASE_MANAGER', 'CARE_WORKER', 'CLIENT'],
    supportingPersonas: ['FAMILY_CARER'],
    surfaces: ['web', 'care-worker-app', 'client-portal'],
    capabilities: ['service.delivery.manage', 'frontline.shift.execute', 'portal.care.view'],
    initiatingPersona: 'COORDINATOR',
    closingPersona: 'CLIENT',
    handoff: 'coordinator -> care worker -> client visit continuity',
    validatedBy: [
      'web schedules planning view',
      'web home-care service-delivery ledger',
      'care-worker-app shift execution baseline',
      'client portal visits visibility surface',
    ],
    mandatoryBoundaries: [
      'services_delivered remains the canonical visit ledger',
      'branch and client attribution stay on the canonical service-delivery path',
      'portal visibility follows provider-safe section rules',
    ],
    optionalProviderBlocks: ['preferred staff guidance', 'local planning templates'],
    clientVisibility: 'client_and_family_visible',
  },
  {
    id: 'clinical-review-follow-through',
    title: 'Clinical review follow-through',
    summary:
      'Nurses, clinical leads, and case managers can review abnormal clinical capture in one queue and translate that review into assessment and care-plan follow-through.',
    tier: 'mandatory',
    primaryPersonas: ['REGISTERED_NURSE', 'ENROLLED_NURSE', 'CLINICAL_LEAD', 'CASE_MANAGER'],
    supportingPersonas: ['TEAM_LEADER', 'FACILITY_MANAGER'],
    surfaces: ['web', 'care-worker-app'],
    capabilities: [
      'frontline.observation.record',
      'frontline.medication.administer',
      'frontline.wound.record',
      'client.care.workspace',
    ],
    initiatingPersona: 'REGISTERED_NURSE',
    closingPersona: 'CASE_MANAGER',
    handoff: 'RN -> clinical lead -> case manager follow-through',
    validatedBy: [
      'web DON clinical workbench',
      'web observations, medications, wounds, assessments, and care-plan continuity summaries',
      'care-worker-app frontline escalation prompts',
    ],
    mandatoryBoundaries: [
      'frontline authoring and clinical review stay separated but connected',
      'assessment and care-plan continuity cannot be bypassed by optional dashboards',
      'review ownership remains role-gated',
    ],
    optionalProviderBlocks: ['local review dashboards', 'discipline-specific escalation emphasis'],
    clientVisibility: 'internal_only',
  },
  {
    id: 'governance-lodgement-to-closeout',
    title: 'Governance lodgement to closeout',
    summary:
      'Incidents, complaints, and feedback now form one governed workflow from frontline or portal lodgement through quality closeout and client/family awareness.',
    tier: 'mandatory',
    primaryPersonas: ['CARE_WORKER', 'QUALITY_OFFICER', 'FACILITY_MANAGER', 'FAMILY_CARER'],
    supportingPersonas: ['CLIENT', 'REGISTERED_NURSE', 'TEAM_LEADER'],
    surfaces: ['care-worker-app', 'web', 'client-portal'],
    capabilities: ['incident.create', 'incident.review', 'complaint.create', 'portal.feedback'],
    initiatingPersona: 'CARE_WORKER',
    closingPersona: 'QUALITY_OFFICER',
    handoff: 'care worker -> incident -> quality officer -> client/family awareness',
    validatedBy: [
      'care-worker-app incident lodgement',
      'web incidents and complaints governance continuity surfaces',
      'web quality hub governance closeout view',
      'client portal feedback and complaint lodgement path',
    ],
    mandatoryBoundaries: [
      'regulated incident evidence and attribution stay mandatory',
      'portal complaint entry does not collapse into generic feedback storage',
      'internal governance closeout remains in web review surfaces',
    ],
    optionalProviderBlocks: ['non-regulated compliments workflow', 'local governance dashboards'],
    clientVisibility: 'client_and_family_visible',
  },
  {
    id: 'finance-statement-visibility',
    title: 'Finance statement visibility',
    summary:
      'Finance officers can manage claims, statements, and prudential follow-through while clients and representatives see the correct financial outputs without crossing into internal finance workflows.',
    tier: 'mandatory',
    primaryPersonas: ['FINANCE_OFFICER', 'CLIENT', 'FAMILY_CARER'],
    supportingPersonas: ['CASE_MANAGER', 'COORDINATOR'],
    surfaces: ['web', 'client-portal'],
    capabilities: [
      'sah.claim.prepare',
      'sah.statement.issue',
      'prudential.access',
      'portal.financial.view',
    ],
    initiatingPersona: 'FINANCE_OFFICER',
    closingPersona: 'CLIENT',
    handoff: 'finance officer -> statement issuance -> client portal visibility',
    validatedBy: [
      'web claims readiness and lifecycle',
      'web monthly statements continuity',
      'web prudential readiness summary',
      'client portal budget, invoices, agreements, and documents visibility',
    ],
    mandatoryBoundaries: [
      'internal claims and prudential workflows remain web-only',
      'client portal visibility follows provider-safe section configuration',
      'statement and financial evidence history remain auditable',
    ],
    optionalProviderBlocks: ['portal invoices visibility', 'agreement document emphasis'],
    clientVisibility: 'client_and_family_visible',
  },
  {
    id: 'tenant-workflow-configuration',
    title: 'Tenant workflow configuration',
    summary:
      'Tenant admins can tailor workflow blocks, labels, approvals, escalations, and portal visibility from approved building blocks without weakening mandatory workflow controls.',
    tier: 'strongly_recommended',
    primaryPersonas: ['TENANT_ADMIN'],
    supportingPersonas: ['COORDINATOR', 'FACILITY_MANAGER', 'CASE_MANAGER'],
    surfaces: ['web'],
    capabilities: ['tenant.configure'],
    initiatingPersona: 'TENANT_ADMIN',
    closingPersona: 'TENANT_ADMIN',
    handoff: 'tenant admin -> workflow configuration -> role-appropriate user experience',
    validatedBy: [
      'web settings workflows',
      'web settings modules',
      'web settings portal',
      'web onboarding operating-model seeding',
    ],
    mandatoryBoundaries: [
      'mandatory workflow blocks remain protected',
      'module toggles cannot disable protected workflow modules',
      'portal mandatory sections remain locked',
    ],
    optionalProviderBlocks: ['labels', 'dashboard focus', 'approval posture', 'escalation posture'],
    clientVisibility: 'not_applicable',
  },
  {
    id: 'portal-messaging-visibility',
    title: 'Portal messaging and visibility',
    summary:
      'Clients, family carers, and case managers can use the portal for safe visibility and messaging without turning it into a second operator console.',
    tier: 'optional',
    primaryPersonas: ['CLIENT', 'FAMILY_CARER', 'CASE_MANAGER'],
    supportingPersonas: ['TENANT_ADMIN'],
    surfaces: ['web', 'client-portal'],
    capabilities: ['portal.messaging', 'portal.documents.view', 'portal.rights.view'],
    initiatingPersona: 'CLIENT',
    closingPersona: 'CASE_MANAGER',
    handoff: 'client/family -> messaging -> case manager follow-up',
    validatedBy: [
      'client portal shell visibility model',
      'client portal dashboard and messages visibility',
      'web portal settings and section gating',
    ],
    mandatoryBoundaries: [
      'portal remains read-mostly plus messaging',
      'contact-person access stays limited to the safe subset',
      'mandatory rights and document visibility stay protected',
    ],
    optionalProviderBlocks: ['messages', 'advance care visibility', 'dashboard emphasis'],
    clientVisibility: 'client_and_family_visible',
  },
] as const;

export function listUncoveredFirstClassWorkflowPersonas(
  storyPacks: readonly WorkflowStoryPack[] = WORKFLOW_STORY_PACKS,
): UserRole[] {
  const covered = new Set<UserRole>();

  for (const storyPack of storyPacks) {
    for (const persona of storyPack.primaryPersonas) {
      covered.add(persona);
    }
  }

  return FIRST_CLASS_WORKFLOW_PERSONAS.filter((persona) => !covered.has(persona));
}

export function getWorkflowStoryPackSummary(
  storyPacks: readonly WorkflowStoryPack[] = WORKFLOW_STORY_PACKS,
): WorkflowStoryPackSummary {
  const uncoveredPersonas = listUncoveredFirstClassWorkflowPersonas(storyPacks);

  return {
    totalPacks: storyPacks.length,
    mandatoryPacks: storyPacks.filter((storyPack) => storyPack.tier === 'mandatory').length,
    stronglyRecommendedPacks: storyPacks.filter(
      (storyPack) => storyPack.tier === 'strongly_recommended',
    ).length,
    optionalPacks: storyPacks.filter((storyPack) => storyPack.tier === 'optional').length,
    crossAppPacks: storyPacks.filter((storyPack) => storyPack.surfaces.length > 1).length,
    coveredPersonas: FIRST_CLASS_WORKFLOW_PERSONAS.length - uncoveredPersonas.length,
    uncoveredPersonas,
    requiredHandoffsCovered: hasRequiredWorkflowHandoffCoverage(storyPacks),
  };
}

export function hasRequiredWorkflowStoryPacks(
  storyPacks: readonly WorkflowStoryPack[] = WORKFLOW_STORY_PACKS,
): boolean {
  const availableIds = new Set(storyPacks.map((storyPack) => storyPack.id));
  return REQUIRED_WORKFLOW_STORY_PACK_IDS.every((storyPackId) => availableIds.has(storyPackId));
}

export function hasRequiredWorkflowHandoffCoverage(
  storyPacks: readonly WorkflowStoryPack[] = WORKFLOW_STORY_PACKS,
): boolean {
  const availableHandoffs = new Set(storyPacks.map((storyPack) => storyPack.handoff));
  return PRIORITY_WORKFLOW_HANDOFFS.every((handoff) => availableHandoffs.has(handoff));
}
